<template>
  <Button
    :type="type"
    :variant="currentVariant"
    :button-style="currentStyle"
    :icon="undefined"
    :disabled="currentDisabled"
    :custom-size="customSize"
    :rounded="rounded"
    v-bind="{ 'aria-busy': isLoading }"
    @click="onClick"
  >
    <template v-if="isLoading">
      <span class="position-relative d-inline-flex w-100 align-items-center justify-content-center">
        <span class="invisible"><slot /></span>
        <i class="fa-solid fa-spinner fa-spin loading-center" aria-hidden="true"></i>
        <span class="visually-hidden">Loading</span>
      </span>
    </template>
    <template v-else>
      <template v-if="mode === 'apply' && isAppliedEffective">
        <slot name="applied">応募済み</slot>
      </template>
      <template
        v-else-if="
          (mode === 'consider-navigate' || mode === 'consider-toggle') && isConsideringEffective
        "
      >
        <slot name="considering">検討中</slot>
      </template>
      <template v-else>
        <slot />
      </template>
    </template>
  </Button>
</template>

<script setup lang="ts">
import { computed, nextTick, ref } from 'vue';
import Button from './Button.vue';
import type { ButtonProps, ButtonStyle, ButtonVariant } from './types';

type LoadingMode = 'apply' | 'consider-navigate' | 'consider-toggle';

interface LoadingButtonProps extends ButtonProps {
  action: () => Promise<unknown>;
  mode?: LoadingMode;
  considering?: boolean;
  applied?: boolean;
  initialConsidering?: boolean;
  initialApplied?: boolean;
}

const props = withDefaults(defineProps<LoadingButtonProps>(), {
  type: 'button',
  variant: 'primary',
  buttonStyle: 'filled',
  icon: undefined,
  disabled: false,
  customSize: false,
  rounded: true,
  mode: 'apply',
});

const emit = defineEmits(['click', 'done', 'error', 'navigate', 'toggle']);

const isLoading = ref(false);
const hasApplied = ref<boolean>(props.initialApplied ?? false);
const isConsidering = ref<boolean>(props.initialConsidering ?? false);

const mode = computed<LoadingMode>(() => props.mode ?? 'apply');

const isAppliedEffective = computed<boolean>(() => props.applied ?? hasApplied.value);
const isConsideringEffective = computed<boolean>(() => props.considering ?? isConsidering.value);

const currentVariant = computed<ButtonVariant>(() => {
  if (mode.value === 'apply' && isAppliedEffective.value) {
    return 'gray';
  }
  if (mode.value === 'consider-navigate' && isConsideringEffective.value) {
    return 'gray';
  }
  return props.variant ?? 'primary';
});

const currentStyle = computed<ButtonStyle>(() => {
  if (
    (mode.value === 'apply' && isAppliedEffective.value) ||
    ((mode.value === 'consider-navigate' || mode.value === 'consider-toggle') &&
      isConsideringEffective.value)
  ) {
    return 'filled';
  }
  return props.buttonStyle ?? 'filled';
});

const currentDisabled = computed<boolean>(() => {
  if (isLoading.value) return true;
  if (props.disabled) return true;
  if (mode.value === 'apply' && isAppliedEffective.value) return true;
  return false;
});

const onClick = async (event: MouseEvent) => {
  emit('click', event);
  if (isLoading.value) return;

  if (mode.value === 'consider-navigate' && isConsideringEffective.value) {
    emit('navigate');
    return;
  }

  try {
    isLoading.value = true;
    await props.action();

    if (mode.value === 'apply') {
      if (props.applied === undefined) hasApplied.value = true;
    } else if (mode.value === 'consider-navigate') {
      if (props.considering === undefined) isConsidering.value = true;
    } else {
      if (props.considering === undefined) {
        isConsidering.value = !isConsidering.value;
        emit('toggle', isConsidering.value);
      } else {
        emit('toggle', !isConsideringEffective.value);
      }
    }

    await nextTick();
    emit('done');
  } catch (err) {
    emit('error', err);
  } finally {
    isLoading.value = false;
  }
};

const type = computed(() => props.type);
</script>

<style scoped>
.loading-center {
  position: absolute;
  inset: 0;
  margin: auto;
  height: 1em;
  width: 1em;
}
</style>
